import ngeohash from 'ngeohash';
import {
  ZoneGeographique,
  LocalisationUtilisateur,
  SEUILS_ENGAGEMENT
} from '../types/analytics.types';

/**
 * Service de calcul et gestion des zones géographiques
 * Utilise le système Geohash pour l'indexation spatiale optimisée
 * Precision 6 = ~1.2km x 0.6km (cohérent avec la migration Supabase)
 */
export class ZoneGeographiqueService {
  private static readonly RAYON_ZONE_KM = SEUILS_ENGAGEMENT.zone_geographique_rayon;
  private static readonly GEOHASH_PRECISION = 6; // ~1.2km x 0.6km

  /**
   * Calculer le geohash basé sur les coordonnées
   * Precision 6 pour cohérence avec la migration Supabase 006_geohash_indexing.sql
   * @returns Geohash de précision 6 (ex: "u09tvw")
   */
  static getGeohash(lat: number, lng: number, precision: number = this.GEOHASH_PRECISION): string {
    if (!this.validerCoordonnees(lat, lng)) {
      return 'unknown';
    }
    return ngeohash.encode(lat, lng, precision);
  }

  /**
   * @deprecated Utiliser getGeohash() à la place
   * Conservé pour rétrocompatibilité temporaire
   */
  static getZoneId(lat: number, lng: number): string {
    return this.getGeohash(lat, lng);
  }

  /**
   * Calculer le centre de zone depuis un geohash
   * Décode le geohash pour obtenir les coordonnées centrales
   */
  static getCentreZone(lat: number, lng: number): { lat: number, lng: number } {
    const geohash = this.getGeohash(lat, lng);
    if (geohash === 'unknown') {
      return { lat, lng };
    }
    const decoded = ngeohash.decode(geohash);
    return { lat: decoded.latitude, lng: decoded.longitude };
  }

  /**
   * Décoder un geohash en coordonnées
   */
  static decodeGeohash(geohash: string): { lat: number, lng: number } | null {
    try {
      const decoded = ngeohash.decode(geohash);
      return { lat: decoded.latitude, lng: decoded.longitude };
    } catch {
      return null;
    }
  }

  /**
   * Créer un objet ZoneGeographique complet
   */
  static createZone(lat: number, lng: number): ZoneGeographique {
    const centre = this.getCentreZone(lat, lng);
    return {
      id: this.getGeohash(lat, lng),
      latitude: centre.lat,
      longitude: centre.lng,
      rayon: this.RAYON_ZONE_KM
    };
  }

  /**
   * Calculer la distance entre deux points (formule Haversine)
   * Réutilise la logique existante de useGeolocation
   */
  static calculerDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  /**
   * Déterminer si deux points sont dans la même zone 10km
   * Utilisé pour les métriques d'engagement géographique
   */
  static sontDansLaMemeZone(lat1: number, lng1: number, lat2: number, lng2: number): boolean {
    return this.calculerDistance(lat1, lng1, lat2, lng2) <= this.RAYON_ZONE_KM;
  }

  /**
   * Calculer la distance entre l'utilisateur et un événement
   * Utilisé pour les métriques de distance dans les événements analytics
   */
  static calculerDistanceUtilisateurEvenement(
    utilisateur: LocalisationUtilisateur,
    evenement: { latitude?: number; longitude?: number }
  ): number | null {
    if (!evenement.latitude || !evenement.longitude) {
      return null;
    }
    return this.calculerDistance(
      utilisateur.lat,
      utilisateur.lng,
      evenement.latitude,
      evenement.longitude
    );
  }

  /**
   * Détecter le type d'exploration géographique
   * Basé sur le nombre de zones visitées dans une session
   */
  static detecterTypeExploration(zonesVisitees: string[]): 'locale' | 'regionale' | 'large' {
    const nombreZones = zonesVisitees.length;
    
    if (nombreZones <= 1) {
      return 'locale';
    } else if (nombreZones <= 3) {
      return 'regionale';
    } else {
      return 'large';
    }
  }

  /**
   * Calculer la distance totale explorée entre les zones
   * Utilisé pour les métriques d'exploration géographique
   */
  static calculerDistanceTotaleExploree(zones: ZoneGeographique[]): number {
    if (zones.length < 2) return 0;

    let distanceTotale = 0;
    for (let i = 1; i < zones.length; i++) {
      distanceTotale += this.calculerDistance(
        zones[i-1].latitude,
        zones[i-1].longitude,
        zones[i].latitude,
        zones[i].longitude
      );
    }
    return distanceTotale;
  }

  /**
   * Obtenir les geohashes voisins d'un geohash donné
   * Utilise ngeohash.neighbors pour obtenir les 8 cases adjacentes
   */
  static getGeohashNeighbors(geohash: string): string[] {
    try {
      const neighbors = ngeohash.neighbors(geohash);
      return Object.values(neighbors);
    } catch {
      return [];
    }
  }

  /**
   * Obtenir les zones adjacentes (dans un rayon de 20km)
   * Utilise le décodage de geohash pour calculer les distances
   */
  static getZonesAdjacentes(geohashId: string, autresGeohashes: string[]): string[] {
    const decoded = this.decodeGeohash(geohashId);
    if (!decoded) return [];

    return autresGeohashes.filter(autreGeohash => {
      if (autreGeohash === geohashId) return false;

      const autreDecoded = this.decodeGeohash(autreGeohash);
      if (!autreDecoded) return false;

      const distance = this.calculerDistance(
        decoded.lat, decoded.lng,
        autreDecoded.lat, autreDecoded.lng
      );

      // Zone adjacente = entre 10km et 20km
      return distance > this.RAYON_ZONE_KM && distance <= (this.RAYON_ZONE_KM * 2);
    });
  }

  /**
   * Validation des coordonnées
   * Réutilise la logique de validation de useGeolocation
   */
  static validerCoordonnees(lat: number, lng: number): boolean {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  /**
   * Créer une LocalisationUtilisateur à partir des coordonnées existantes
   * Compatible avec l'infrastructure useGeolocation
   */
  static creerLocalisationUtilisateur(
    location: { latitude: number; longitude: number } | undefined
  ): LocalisationUtilisateur | null {
    if (!location || !this.validerCoordonnees(location.latitude, location.longitude)) {
      return null;
    }
    return {
      lat: location.latitude,
      lng: location.longitude
    };
  }

  /**
   * Calculer les métriques géographiques pour une session
   * Utilisé dans les événements de fin de session
   */
  static calculerMetriquesGeographiques(geohashesVisites: string[]): {
    nombreZones: number;
    typeExploration: 'locale' | 'regionale' | 'large';
    distanceTotale: number;
    rayonMaximal: number;
  } {
    const nombreZones = geohashesVisites.length;
    const typeExploration = this.detecterTypeExploration(geohashesVisites);

    // Convertir les geohashes en objets ZoneGeographique pour les calculs
    const zones = geohashesVisites
      .map(geohash => {
        const decoded = this.decodeGeohash(geohash);
        if (!decoded) return null;
        return this.createZone(decoded.lat, decoded.lng);
      })
      .filter((zone): zone is ZoneGeographique => zone !== null);

    const distanceTotale = this.calculerDistanceTotaleExploree(zones);

    // Calculer le rayon maximal depuis la première zone
    let rayonMaximal = 0;
    if (zones.length > 1) {
      const zoneOrigin = zones[0];
      rayonMaximal = Math.max(...zones.slice(1).map(zone =>
        this.calculerDistance(zoneOrigin.latitude, zoneOrigin.longitude, zone.latitude, zone.longitude)
      ));
    }

    return {
      nombreZones,
      typeExploration,
      distanceTotale,
      rayonMaximal
    };
  }
} 